/**
 * Enhanced Shopify GA4 Data Layer
 * Comprehensive e-commerce tracking with complete purchase data capture
 */

class ShopifyDataLayer {
  constructor(options = {}) {
    this.config = {
      gtag: options.gtag || window.gtag,
      measurementId: options.measurementId || null,
      debugMode: options.debugMode || false,
      customDimensions: options.customDimensions || {},
      currency: options.currency || window.Shopify?.currency?.active || 'USD',
      ...options
    };
    
    // Track processed events to prevent loops
    this.trackedPurchases = new Set();
    this.trackedViewItems = new Set();
    this.trackedAddToCarts = new Set();
    this.lastEventTimes = new Map();
    
    this.init();
  }

  init() {
    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];
    
    // Disable Shopify's native analytics to prevent duplicate events
    this.disableShopifyNativeTracking();
    
    // Set up gtag if not already available
    if (!window.gtag) {
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
    }
    
    // Configure enhanced ecommerce
    this.configureEnhancedEcommerce();
    
    // Set default parameters
    this.setDefaultParameters();
    
    this.log('Shopify Data Layer initialized');
  }

  configureEnhancedEcommerce() {
    if (this.config.measurementId) {
      window.gtag('config', this.config.measurementId, {
        currency: this.config.currency,
        enhanced_ecommerce: true,
        send_page_view: true,
        custom_map: this.config.customDimensions
      });
    }
  }

  setDefaultParameters() {
    const defaultParams = {
      currency: this.config.currency,
      country: window.Shopify?.country || 'US',
      language: window.Shopify?.locale || 'en',
      shop_domain: window.Shopify?.shop || window.location.hostname,
      page_type: this.getPageType(),
      customer_id: window.ShopifyAnalytics?.meta?.page?.customerId || null
    };

    window.gtag('set', defaultParams);
  }

  getPageType() {
    if (window.ShopifyAnalytics?.meta?.page?.pageType) {
      return window.ShopifyAnalytics.meta.page.pageType;
    }
    
    const path = window.location.pathname;
    if (path.includes('/products/')) return 'product';
    if (path.includes('/collections/')) return 'collection';
    if (path.includes('/cart')) return 'cart';
    if (path.includes('/checkout')) return 'checkout';
    if (path.includes('/search')) return 'search';
    if (path.includes('/account')) return 'account';
    if (path.includes('/thank')) return 'purchase';
    if (path === '/') return 'home';
    return 'other';
  }

  // Enhanced Purchase Tracking - Captures ALL purchase details
  purchase(orderData) {
    if (!orderData) {
      this.log('Purchase tracking failed: No order data provided', 'warn');
      return;
    }

    // More lenient validation for sandbox environments
    if (!this.isValidPurchaseDataSandbox(orderData)) {
      this.log('Data appears to be cart data, not purchase. Skipping.', 'warn');
      return;
    }

    const enhancedOrderData = this.enhanceOrderData(orderData);
    
    if (!enhancedOrderData) {
      this.log('Purchase tracking failed: Could not enhance order data', 'warn');
      return;
    }
    
    // In sandbox, track even if no items but has total value
    if ((!enhancedOrderData.items || enhancedOrderData.items.length === 0) && 
        (!enhancedOrderData.value || enhancedOrderData.value <= 0)) {
      this.log('Purchase tracking failed: No items and no value in order', 'warn');
      return;
    }

    // Create unique identifier for this purchase
    const purchaseId = this.createPurchaseId(enhancedOrderData);
    
    // Check if we've already tracked this purchase
    if (this.trackedPurchases.has(purchaseId)) {
      this.log(`Purchase ${purchaseId} already tracked, skipping duplicate`, 'warn');
      return;
    }
    
    // Mark this purchase as tracked
    this.trackedPurchases.add(purchaseId);

    // GA4 Purchase Event
    const purchaseEvent = {
      event: 'purchase',
      ecommerce: {
        transaction_id: enhancedOrderData.transaction_id,
        affiliation: enhancedOrderData.affiliation,
        value: enhancedOrderData.value,
        tax: enhancedOrderData.tax,
        shipping: enhancedOrderData.shipping,
        currency: enhancedOrderData.currency,
        coupon: enhancedOrderData.coupon,
        items: enhancedOrderData.items
      }
    };

    // Add customer data if available
    if (enhancedOrderData.customer_data) {
      purchaseEvent.customer_data = enhancedOrderData.customer_data;
    }

    // Add custom dimensions
    if (this.config.customDimensions) {
      Object.assign(purchaseEvent, this.config.customDimensions);
    }

    // Send to GA4 (only use dataLayer.push to avoid duplicates)
    window.dataLayer.push(purchaseEvent);

    // Only send additional events if this is a new purchase (not a duplicate)
    // These detailed events are for internal analytics, not essential for GA4

    this.log('Purchase tracked successfully:', enhancedOrderData);
    
    // Store for backup/recovery
    this.storePurchaseData(enhancedOrderData);
  }

  enhanceOrderData(orderData) {
    const enhanced = {
      transaction_id: orderData.transaction_id || orderData.id || orderData.order_number || orderData.checkout_id || 'unknown_' + Date.now(),
      affiliation: orderData.affiliation || orderData.shop?.name || window.Shopify?.shop || 'Shopify Store',
      value: this.parsePrice(orderData.value || orderData.total_price || orderData.subtotal_price || orderData.current_total_price || 0),
      tax: this.parsePrice(orderData.tax || orderData.total_tax || orderData.tax_price || 0),
      shipping: this.parsePrice(orderData.shipping || orderData.total_shipping || orderData.shipping_price || 0),
      currency: orderData.currency || orderData.presentment_currency || this.config.currency,
      coupon: this.extractCoupons(orderData.discount_codes || orderData.discounts || []),
      items: this.enhanceOrderItems(orderData.items || orderData.line_items || []),
      timestamp: new Date().toISOString()
    };

    // Add customer information if available
    if (orderData.customer || orderData.billing_address || orderData.customer_data) {
      enhanced.customer_data = this.extractCustomerData(orderData);
    }

    // Calculate total if missing
    if (!enhanced.value || enhanced.value === 0) {
      enhanced.value = enhanced.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    }

    return enhanced;
  }

  createPurchaseId(orderData) {
    // Create unique ID based on transaction ID and total value
    const id = orderData.transaction_id || 'unknown';
    const value = orderData.value || 0;
    const itemCount = orderData.items ? orderData.items.length : 0;
    
    return `${id}_${value}_${itemCount}`;
  }

  isValidPurchaseData(orderData) {
    // Check if we're on a purchase confirmation page
    const currentPage = window.location.pathname;
    const isThankYouPage = currentPage.includes('/thank') || 
                          currentPage.includes('/orders/') ||
                          document.querySelector('.checkout-thank-you, .order-confirmation, .thank-you') !== null;
    
    // Must have a valid transaction ID (not just a timestamp)
    const hasValidTransactionId = orderData.transaction_id || 
                                 orderData.id || 
                                 orderData.order_number || 
                                 orderData.order_id;
    
    // Should not be cart data
    const isNotCartData = !orderData.token && // Cart tokens indicate cart data
                         !currentPage.includes('/cart') &&
                         orderData.checkout_id !== undefined; // Cart data usually lacks checkout_id
    
    // Check for purchase-specific fields that cart data doesn't have
    const hasPurchaseFields = orderData.financial_status ||
                             orderData.fulfillment_status ||
                             orderData.processed_at ||
                             orderData.created_at ||
                             orderData.customer ||
                             orderData.billing_address ||
                             orderData.shipping_address;
    
    // Must meet at least one of these criteria
    const validCriteria = [
      isThankYouPage,
      hasPurchaseFields,
      (hasValidTransactionId && isNotCartData && orderData.total_price > 0)
    ];
    
    const isValid = validCriteria.some(criteria => criteria);
    
    if (!isValid) {
      this.log('Purchase validation failed:', {
        isThankYouPage,
        hasValidTransactionId,
        isNotCartData,
        hasPurchaseFields,
        currentPage,
        orderDataKeys: Object.keys(orderData)
      });
    }
    
    return isValid;
  }

  enhanceOrderItems(items) {
    if (!Array.isArray(items)) return [];

    return items.map((item, index) => ({
      item_id: String(item.item_id || item.id || item.variant_id || item.product_id || index),
      item_name: String(item.item_name || item.title || item.product_title || item.name || 'Unknown Product'),
      item_category: String(item.item_category || item.product_type || item.type || 'Unknown'),
      item_category2: String(item.item_category2 || item.vendor || item.brand || ''),
      item_brand: String(item.item_brand || item.vendor || item.brand || ''),
      item_variant: String(item.item_variant || item.variant_title || item.variant || ''),
      price: this.parsePrice(item.price || item.final_price || item.line_price || 0),
      quantity: parseInt(item.quantity) || 1,
      index: index,
      sku: String(item.sku || ''),
      product_id: String(item.product_id || ''),
      variant_id: String(item.variant_id || item.id || ''),
      url: String(item.url || ''),
      image: String(item.image || ''),
      handle: String(item.handle || ''),
      grams: parseInt(item.grams) || 0,
      compare_at_price: this.parsePrice(item.compare_at_price || 0),
      discount_amount: this.parsePrice(item.discount_amount || item.total_discount || 0)
    }));
  }

  extractCustomerData(orderData) {
    const customer = orderData.customer || {};
    const billing = orderData.billing_address || {};
    const shipping = orderData.shipping_address || {};
    
    return {
      customer_id: String(customer.id || orderData.customer_id || ''),
      email: String(customer.email || orderData.email || ''),
      phone: String(customer.phone || orderData.phone || ''),
      first_name: String(customer.first_name || billing.first_name || ''),
      last_name: String(customer.last_name || billing.last_name || ''),
      city: String(billing.city || customer.default_address?.city || ''),
      state: String(billing.province || billing.state || customer.default_address?.province || ''),
      country: String(billing.country || customer.default_address?.country || ''),
      postal_code: String(billing.zip || billing.postal_code || customer.default_address?.zip || ''),
      shipping_city: String(shipping.city || ''),
      shipping_state: String(shipping.province || shipping.state || ''),
      shipping_country: String(shipping.country || ''),
      total_spent: this.parsePrice(customer.total_spent || 0),
      orders_count: parseInt(customer.orders_count) || 0,
      accepts_marketing: Boolean(customer.accepts_marketing)
    };
  }

  extractCoupons(discounts) {
    if (!discounts || !Array.isArray(discounts)) return '';
    return discounts.map(d => d.code || d.title || d.name).filter(Boolean).join(', ');
  }

  parsePrice(price) {
    if (typeof price === 'number') return parseFloat(price.toFixed(2));
    if (typeof price === 'string') {
      const cleanPrice = price.replace(/[^\d.-]/g, '');
      const numPrice = parseFloat(cleanPrice);
      // Handle Shopify's price format (cents)
      return numPrice > 1000 ? parseFloat((numPrice / 100).toFixed(2)) : parseFloat(numPrice.toFixed(2));
    }
    return 0;
  }

  // Other GA4 Events
  viewItem(product) {
    if (!product) return;

    // Create unique identifier for this view item event
    const viewItemId = this.createViewItemId(product);
    
    // Prevent duplicate view item events (within 30 seconds)
    if (this.isDuplicateEvent('view_item', viewItemId, 30000)) {
      this.log(`View item ${viewItemId} already tracked recently, skipping duplicate`);
      return;
    }

    const event = {
      event: 'view_item',
      ecommerce: {
        currency: this.config.currency,
        value: this.parsePrice(product.price || 0),
        items: [{
          item_id: String(product.id || product.variant_id || product.product_id),
          item_name: String(product.title || product.name),
          item_category: String(product.type || product.product_type || ''),
          item_brand: String(product.vendor || product.brand || ''),
          item_variant: String(product.variant_title || product.variant || ''),
          price: this.parsePrice(product.price || 0),
          quantity: parseInt(product.quantity) || 1
        }]
      }
    };

    // Mark this event as tracked
    this.markEventTracked('view_item', viewItemId);

    window.dataLayer.push(event);
    this.log('View item tracked:', product);
  }

  viewItemList(products, listName = '', listId = '') {
    if (!products || !Array.isArray(products)) return;

    const items = products.map((product, index) => ({
      item_id: String(product.id || product.variant_id || product.product_id || index),
      item_name: String(product.title || product.name || 'Unknown'),
      item_category: String(product.type || product.product_type || ''),
      item_brand: String(product.vendor || product.brand || ''),
      item_variant: String(product.variant_title || product.variant || ''),
      price: this.parsePrice(product.price || 0),
      index: product.position || index,
      item_list_name: String(listName),
      item_list_id: String(listId)
    }));

    const event = {
      event: 'view_item_list',
      ecommerce: {
        item_list_name: String(listName),
        item_list_id: String(listId),
        items: items
      }
    };

    window.dataLayer.push(event);
    this.log('View item list tracked:', { listName, products: products.length });
  }

  addToCart(product) {
    if (!product) return;

    // Create unique identifier for this add to cart event
    const addToCartId = this.createAddToCartId(product);
    
    // Prevent duplicate add to cart events (within 3 seconds for more precision)
    if (this.isDuplicateEvent('add_to_cart', addToCartId, 3000)) {
      this.log(`Add to cart ${addToCartId} already tracked recently, skipping duplicate`);
      return;
    }

    const event = {
      event: 'add_to_cart',
      ecommerce: {
        currency: this.config.currency,
        value: this.parsePrice(product.price * (product.quantity || 1)),
        items: [{
          item_id: String(product.id || product.variant_id || product.product_id),
          item_name: String(product.title || product.name),
          item_category: String(product.type || product.product_type || ''),
          item_brand: String(product.vendor || product.brand || ''),
          item_variant: String(product.variant_title || product.variant || ''),
          price: this.parsePrice(product.price || 0),
          quantity: parseInt(product.quantity) || 1
        }]
      }
    };

    // Mark this event as tracked
    this.markEventTracked('add_to_cart', addToCartId);

    window.dataLayer.push(event);
    this.log('Add to cart tracked:', product);
  }

  removeFromCart(product) {
    if (!product) return;

    const event = {
      event: 'remove_from_cart',
      ecommerce: {
        currency: this.config.currency,
        value: this.parsePrice(product.price * (product.quantity || 1)),
        items: [{
          item_id: String(product.id || product.variant_id || product.product_id),
          item_name: String(product.title || product.name),
          item_category: String(product.type || product.product_type || ''),
          item_brand: String(product.vendor || product.brand || ''),
          item_variant: String(product.variant_title || product.variant || ''),
          price: this.parsePrice(product.price || 0),
          quantity: parseInt(product.quantity) || 1
        }]
      }
    };

    window.dataLayer.push(event);
    this.log('Remove from cart tracked:', product);
  }

  viewCart(cart) {
    if (!cart || !cart.items) return;

    // Only track cart view on cart page or when drawer is actually opened
    const currentPageType = this.getPageType();
    const isCartContext = currentPageType === 'cart' || 
                         window.location.pathname.includes('/cart') ||
                         this.isCartDrawerContext();
    
    if (!isCartContext) {
      this.log('Skipping view_cart - not in cart context. Page type:', currentPageType);
      return;
    }

    // Create unique identifier for this cart view
    const cartId = this.createCartViewId(cart);
    
    // Prevent duplicate cart views (within 10 seconds)
    if (this.isDuplicateEvent('view_cart', cartId, 10000)) {
      this.log(`Cart view ${cartId} already tracked recently, skipping duplicate`);
      return;
    }

    const items = cart.items.map((item, index) => ({
      item_id: String(item.id || item.variant_id || item.product_id || index),
      item_name: String(item.title || item.product_title || item.name),
      item_category: String(item.product_type || item.type || ''),
      item_brand: String(item.vendor || item.brand || ''),
      item_variant: String(item.variant_title || item.variant || ''),
      price: this.parsePrice(item.price || item.final_price || 0),
      quantity: parseInt(item.quantity) || 1
    }));

    const event = {
      event: 'view_cart',
      ecommerce: {
        currency: cart.currency || this.config.currency,
        value: this.parsePrice(cart.total_price || 0),
        items: items
      }
    };

    // Mark this event as tracked
    this.markEventTracked('view_cart', cartId);

    window.dataLayer.push(event);
    this.log('View cart tracked:', { items: items.length, total: cart.total_price });
  }

  beginCheckout(cart) {
    if (!cart || !cart.items) return;

    const items = cart.items.map((item, index) => ({
      item_id: String(item.id || item.variant_id || item.product_id || index),
      item_name: String(item.title || item.product_title || item.name),
      item_category: String(item.product_type || item.type || ''),
      item_brand: String(item.vendor || item.brand || ''),
      item_variant: String(item.variant_title || item.variant || ''),
      price: this.parsePrice(item.price || item.final_price || 0),
      quantity: parseInt(item.quantity) || 1
    }));

    const event = {
      event: 'begin_checkout',
      ecommerce: {
        currency: cart.currency || this.config.currency,
        value: this.parsePrice(cart.total_price || 0),
        items: items
      }
    };

    window.dataLayer.push(event);
    this.log('Begin checkout tracked:', { items: items.length, total: cart.total_price });
  }

  search(searchTerm, results = []) {
    const event = {
      event: 'search',
      search_term: String(searchTerm),
      number_of_results: parseInt(results.length) || 0
    };

    window.dataLayer.push(event);
    this.log('Search tracked:', { term: searchTerm, results: results.length });
  }

  login(method = 'email') {
    const event = {
      event: 'login',
      method: String(method)
    };

    window.dataLayer.push(event);
    this.log('Login tracked:', { method });
  }

  signUp(method = 'email') {
    const event = {
      event: 'sign_up',
      method: String(method)
    };

    window.dataLayer.push(event);
    this.log('Sign up tracked:', { method });
  }

  customEvent(eventName, parameters = {}) {
    const event = {
      event: String(eventName),
      ...parameters,
      timestamp: new Date().toISOString()
    };

    window.dataLayer.push(event);
    this.log(`Custom event tracked: ${eventName}`, parameters);
  }

  // Utility Methods
  storePurchaseData(orderData) {
    try {
      const purchaseHistory = JSON.parse(localStorage.getItem('shopify_purchase_history') || '[]');
      purchaseHistory.push({
        ...orderData,
        tracked_at: new Date().toISOString()
      });
      
      // Keep only last 10 purchases
      if (purchaseHistory.length > 10) {
        purchaseHistory.splice(0, purchaseHistory.length - 10);
      }
      
      localStorage.setItem('shopify_purchase_history', JSON.stringify(purchaseHistory));
    } catch (e) {
      this.log('Failed to store purchase data:', e, 'warn');
    }
  }

  log(message, data = '', level = 'info') {
    if (this.config.debugMode) {
      const logMethod = level === 'warn' ? console.warn : level === 'error' ? console.error : console.log;
      logMethod(`[Shopify Data Layer] ${message}`, data);
    }
  }

  // Get stored purchase data for recovery
  getPurchaseHistory() {
    try {
      return JSON.parse(localStorage.getItem('shopify_purchase_history') || '[]');
    } catch (e) {
      return [];
    }
  }

  // Clear stored data
  clearPurchaseHistory() {
    try {
      localStorage.removeItem('shopify_purchase_history');
      this.log('Purchase history cleared');
    } catch (e) {
      this.log('Failed to clear purchase history:', e, 'warn');
    }
  }
  
  // Duplicate Event Prevention Methods
  createViewItemId(product) {
    const id = product.id || product.variant_id || product.product_id || 'unknown';
    const url = window.location.pathname;
    return `viewitem_${id}_${url}`;
  }
  
  createAddToCartId(product) {
    const id = product.id || product.variant_id || product.product_id || 'unknown';
    const quantity = product.quantity || 1;
    const price = product.price || 0;
    // Group by 2-second intervals to catch rapid duplicates more effectively
    const timeGroup = Math.floor(Date.now() / 2000);
    return `addtocart_${id}_${quantity}_${price}_${timeGroup}`;
  }
  
  createCartViewId(cart) {
    const itemCount = cart.items ? cart.items.length : 0;
    const totalPrice = cart.total_price || 0;
    const url = window.location.pathname;
    return `viewcart_${itemCount}_${totalPrice}_${url}`;
  }
  
  isDuplicateEvent(eventType, eventId, timeWindow = 30000) {
    const now = Date.now();
    const key = `${eventType}_${eventId}`;
    const lastTime = this.lastEventTimes.get(key);
    
    if (lastTime && (now - lastTime) < timeWindow) {
      return true; // Duplicate within time window
    }
    
    return false;
  }
  
  markEventTracked(eventType, eventId) {
    const now = Date.now();
    const key = `${eventType}_${eventId}`;
    this.lastEventTimes.set(key, now);
    
    // Clean up old entries (keep only last hour)
    const oneHourAgo = now - (60 * 60 * 1000);
    for (const [key, time] of this.lastEventTimes.entries()) {
      if (time < oneHourAgo) {
        this.lastEventTimes.delete(key);
      }
    }
  }
  
  isCartDrawerContext() {
    // Check if cart drawer is currently visible
    const cartDrawer = document.querySelector('.cart-drawer, #cart-drawer, [data-cart-drawer]');
    if (!cartDrawer) return false;
    
    const style = window.getComputedStyle(cartDrawer);
    const hasVisibleClass = cartDrawer.classList.contains('open') || 
                           cartDrawer.classList.contains('active') || 
                           cartDrawer.classList.contains('is-open') ||
                           cartDrawer.classList.contains('drawer-open');
    const hasVisibleStyle = style.display !== 'none' && 
                           style.visibility !== 'hidden' && 
                           parseFloat(style.opacity) > 0;
    
    return hasVisibleClass || hasVisibleStyle;
  }
  
  disableShopifyNativeTracking() {
    // Disable Shopify's built-in analytics to prevent duplicate events
    
    // Method 1: Override ShopifyAnalytics methods
    if (window.ShopifyAnalytics) {
      // Store original methods in case we need them
      window.ShopifyAnalytics._originalPublish = window.ShopifyAnalytics.publish;
      
      // Override the publish method to filter out duplicate events
      window.ShopifyAnalytics.publish = (eventName, payload) => {
        // Block duplicate ecommerce events that we handle ourselves
        if (['Added Product', 'Product Added', 'add_to_cart'].includes(eventName)) {
          this.log(`Blocked Shopify native event: ${eventName}`, payload);
          return;
        }
        
        // Allow other Shopify events to pass through
        return window.ShopifyAnalytics._originalPublish(eventName, payload);
      };
    }
    
    // Method 2: Block GA4 events from Shopify's native integration
    const originalDataLayerPush = window.dataLayer.push;
    window.dataLayer.push = function(eventData) {
      // Block events that look like Shopify's native GA4 integration
      if (eventData && eventData.eventModel && 
          eventData.eventModel.ecomm_prodid && 
          eventData.eventModel.ecomm_totalvalue) {
        
        // This is Shopify's native GA4 format - block it
        window.shopifyDataLayer?.log('Blocked Shopify native GA4 event:', eventData);
        return;
      }
      
      // Allow all other events through
      return originalDataLayerPush.call(this, eventData);
    };
    
    // Method 3: Disable specific Shopify tracking features
    if (window.Shopify && window.Shopify.analytics) {
      // Disable specific analytics features that might cause duplicates
      window.Shopify.analytics.replayQueue = [];
      if (window.Shopify.analytics.publish) {
        window.Shopify.analytics._originalPublish = window.Shopify.analytics.publish;
        window.Shopify.analytics.publish = function(eventName, payload) {
          if (['add_to_cart', 'product_added'].includes(eventName)) {
            return; // Block these events
          }
          return window.Shopify.analytics._originalPublish(eventName, payload);
        };
      }
    }
    
    this.log('Shopify native tracking disabled to prevent duplicates');
  }
}

// Auto-initialize with global access
window.shopifyDataLayer = window.shopifyDataLayer || new ShopifyDataLayer({
  debugMode: window.location.search.includes('debug=true'),
  measurementId: window.ga4MeasurementId || null
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ShopifyDataLayer;
}